import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, Animated, Easing, Modal } from 'react-native';
import { useTranslation } from 'react-i18next';
// @ts-ignore
import { productService } from '../services/ProductService';
import type { ProductDisplay } from '../models';

const bepaalCijferKleur = (grade: string) => {
  if (!grade) return '#999';
  if (grade.startsWith('A')) return '#10B981';
  if (grade.startsWith('B')) return '#34D399';
  if (grade.startsWith('C')) return '#FBBF24';
  if (grade.startsWith('D')) return '#F59E0B';
  return '#EF4444';
};

/* -------------------------------------------------------------------------- */
/*                              LAYOUT WISSELAAR                              */
/* -------------------------------------------------------------------------- */

const LayoutWisselaar: React.FC<{ isPc: boolean, bijWissel: () => void }> = ({ isPc, bijWissel }) => (
  <TouchableOpacity onPress={bijWissel} style={wisselStijlen.container}>
    <Text style={wisselStijlen.tekst}>{isPc ? '🖥️ PC Modus' : '📱 Mobiele Modus'}</Text>
  </TouchableOpacity>
);

const wisselStijlen = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 24,
    right: 24,
    backgroundColor: 'rgba(255,255,255,0.15)',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 30,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
    zIndex: 10,
    cursor: 'pointer',
    // @ts-ignore
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  tekst: {
    color: 'white',
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.5,
  }
});

/* -------------------------------------------------------------------------- */
/*                              TAAL WISSELAAR                                */
/* -------------------------------------------------------------------------- */

const TaalWisselaar: React.FC<{ huidigeTaal: string, bijWissel: (taal: string) => void }> = ({ huidigeTaal, bijWissel }) => (
  <View style={taalStijlen.container}>
    <TouchableOpacity 
      onPress={() => bijWissel('en')} 
      style={[taalStijlen.knop, huidigeTaal === 'en' && taalStijlen.actieveKnop]}
    >
      <Text style={[taalStijlen.tekst, huidigeTaal === 'en' && taalStijlen.actieveTekst]}>EN</Text>
    </TouchableOpacity>
    <View style={taalStijlen.verdeler} />
    <TouchableOpacity 
      onPress={() => bijWissel('nl')} 
      style={[taalStijlen.knop, huidigeTaal === 'nl' && taalStijlen.actieveKnop]}
    >
      <Text style={[taalStijlen.tekst, huidigeTaal === 'nl' && taalStijlen.actieveTekst]}>NL</Text>
    </TouchableOpacity>
  </View>
);

const taalStijlen = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 24,
    left: 24,
    flexDirection: 'row',
    backgroundColor: 'rgba(255,255,255,0.15)',
    borderRadius: 30,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
    zIndex: 10,
    // @ts-ignore
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  knop: {
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  actieveKnop: {
    backgroundColor: 'rgba(255,255,255,0.3)',
  },
  tekst: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13,
    fontWeight: '700',
  },
  actieveTekst: {
    color: 'white',
  },
  verdeler: {
    width: 1,
    backgroundColor: 'rgba(255,255,255,0.2)',
  }
});

/* -------------------------------------------------------------------------- */
/*                              BARCODE ANIMATIE                              */
/* -------------------------------------------------------------------------- */

const BarcodeAnimatie: React.FC<{ isAanHetScannen: boolean }> = ({ isAanHetScannen }) => {
  const laserAnimatie = useRef(new Animated.Value(0)).current;
  const polsAnimatie = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Laser animatie (pew pew)
    Animated.loop(
      Animated.sequence([
        Animated.timing(laserAnimatie, {
          toValue: 1,
          duration: 1500,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
        Animated.timing(laserAnimatie, {
          toValue: 0,
          duration: 1500,
          easing: Easing.inOut(Easing.quad),
          useNativeDriver: false,
        }),
      ])
    ).start();

    // Hartslag animatie
    Animated.loop(
      Animated.sequence([
        Animated.timing(polsAnimatie, {
          toValue: 1.05,
          duration: 1000,
          useNativeDriver: false,
        }),
        Animated.timing(polsAnimatie, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: false,
        }),
      ])
    ).start();
  }, []);

  const laserTranslateY = laserAnimatie.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 100],
  });

  return (
    <Animated.View style={[animatieStijlen.barcodeContainer, { transform: [{ scale: polsAnimatie }] }]}>
      {/* Barcode lijntjes */}
      <View style={animatieStijlen.barcodeLijnen}>
        {[12, 8, 15, 6, 12, 9, 14, 7, 11, 8, 13, 6, 10, 15, 8, 12, 9, 14, 7, 11].map((hoogte, i) => (
          <View
            key={i}
            style={[
              animatieStijlen.barcodeLijn,
              { 
                height: hoogte * 6,
                width: i % 3 === 0 ? 4 : 2,
                backgroundColor: i % 5 === 0 ? '#1a1a2e' : '#333',
              }
            ]}
          />
        ))}
      </View>
      
      {/* Laserstraal */}
      <Animated.View
        style={[
          animatieStijlen.scanLaser,
          { transform: [{ translateY: laserTranslateY }] }
        ]}
      >
        <View style={animatieStijlen.laserLijn} />
        <View style={animatieStijlen.laserGloed} />
      </Animated.View>

      {/* Hoekjes */}
      <View style={[animatieStijlen.hoek, animatieStijlen.linksBoven]} />
      <View style={[animatieStijlen.hoek, animatieStijlen.rechtsBoven]} />
      <View style={[animatieStijlen.hoek, animatieStijlen.linksOnder]} />
      <View style={[animatieStijlen.hoek, animatieStijlen.rechtsOnder]} />
    </Animated.View>
  );
};

const animatieStijlen = StyleSheet.create({
  barcodeContainer: {
    width: 200,
    height: 120,
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  barcodeLijnen: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: 100,
    gap: 2,
  },
  barcodeLijn: {
    backgroundColor: '#1a1a2e',
    borderRadius: 1,
  },
  scanLaser: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 4,
    top: 10,
  },
  laserLijn: {
    height: 2,
    backgroundColor: '#EF4444',
    // @ts-ignore
    boxShadow: '0 0 10px #EF4444, 0 0 20px #EF4444',
  },
  laserGloed: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: -10,
    height: 24,
    // @ts-ignore
    backgroundImage: 'linear-gradient(to bottom, rgba(239, 68, 68, 0.3), transparent)',
  },
  hoek: {
    position: 'absolute',
    width: 20,
    height: 20,
    borderColor: '#4361EE',
    borderWidth: 3,
  },
  linksBoven: { top: -5, left: -5, borderRightWidth: 0, borderBottomWidth: 0, borderTopLeftRadius: 8 },
  rechtsBoven: { top: -5, right: -5, borderLeftWidth: 0, borderBottomWidth: 0, borderTopRightRadius: 8 },
  linksOnder: { bottom: -5, left: -5, borderRightWidth: 0, borderTopWidth: 0, borderBottomLeftRadius: 8 },
  rechtsOnder: { bottom: -5, right: -5, borderLeftWidth: 0, borderTopWidth: 0, borderBottomRightRadius: 8 },

});

const ProductCardWeb: React.FC<{ 
  product: ProductDisplay; 
  onPress: () => void;
  isPcLayout: boolean;
}> = ({ product, onPress, isPcLayout }) => {
  const [imageError, setImageError] = useState(false);

  return (
    <TouchableOpacity
      style={[stijlen.productKaart, isPcLayout ? stijlen.productKaartPc : undefined]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      {product.imageUrl && !imageError ? (
          <View style={stijlen.productIcoon}>
            {/* @ts-ignore - React Native Web Plaatje */}
            <img 
              src={product.imageUrl} 
              alt={product.name}
              onError={() => setImageError(true)}
              style={{ width: '100%', height: '100%', borderRadius: 12, objectFit: 'cover' }} 
            />
          </View>
      ) : (
        <View style={stijlen.productIcoon}>
          <Text style={{ fontSize: 28 }}>🧴</Text>
        </View>
      )}
      <View style={stijlen.productInfo}>
        <Text style={stijlen.productNaam} numberOfLines={1}>{product.name}</Text>
        <Text style={stijlen.productMerk}>{product.brand}</Text>
      </View>
      <View style={[stijlen.cijferBadge, { backgroundColor: bepaalCijferKleur(product.grade) }]}>
        <Text style={stijlen.cijferTekst}>{product.grade}</Text>
      </View>
    </TouchableOpacity>
  );
};

/* -------------------------------------------------------------------------- */
/*                               HOOFD COMPONENT                              */
/* -------------------------------------------------------------------------- */

export const HomeScreen: React.FC<any> = ({ navigation }) => {
  const { t, i18n } = useTranslation();
  const [uitgelichteProducten, setUitgelichteProducten] = useState<ProductDisplay[]>([]);
  const [isPcLayout, setIsPcLayout] = useState(false); 
  const schaalAnimatie = useRef(new Animated.Value(1)).current;
  const zweefAnimatie = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    laadUitgelichteProducten();
    
    Animated.loop(
      Animated.sequence([
        Animated.timing(zweefAnimatie, { toValue: -5, duration: 1500, useNativeDriver: false }),
        Animated.timing(zweefAnimatie, { toValue: 5, duration: 1500, useNativeDriver: false }),
      ])
    ).start();
  }, []);

  const laadUitgelichteProducten = async () => {
    try {
      const products = await productService.getDiscoveryProducts(5);
      setUitgelichteProducten(products);
    } catch (e) {
      console.warn('Kon producten niet laden', e);
      // Demo producten gebruiken voor de presentatie
      setUitgelichteProducten([
        { id: 1, name: 'Ocean Breeze Shampoo', brand: 'EcoLife', grade: 'A+', category: 'Hair Care' } as ProductDisplay,
        { id: 2, name: 'Pure Waves Conditioner', brand: 'PureBase', grade: 'A', category: 'Hair Care' } as ProductDisplay,
        { id: 3, name: 'Coral Reef Sunscreen', brand: 'GreenCo', grade: 'B+', category: 'Sun Care' } as ProductDisplay,
      ]);
    }
  };

  const behandelScanKlik = () => {
    Animated.sequence([
      Animated.timing(schaalAnimatie, { toValue: 0.92, duration: 100, useNativeDriver: false }),
      Animated.timing(schaalAnimatie, { toValue: 1, duration: 100, useNativeDriver: false }),
    ]).start(() => {
      navigation.navigate('Scanner');
    });
  };

  const navigeerNaarProduct = (product: ProductDisplay) => {
    navigation.navigate('ProductDetail', { productId: product.id });
  };

  const navigeerNaarZoeken = () => {
    navigation.navigate('SearchTab', { screen: 'Search' });
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', text: '' });

  const showModal = (title: string, text: string) => {
    setModalContent({ title, text });
    setModalVisible(true);
  };

  return (
    <View style={stijlen.container}>
      {/* Premium Header */}
      <View style={stijlen.kop}>
        <View style={stijlen.kopGradient}>
          <TaalWisselaar huidigeTaal={i18n.language || 'en'} bijWissel={(taal) => i18n.changeLanguage(taal)} />
          <LayoutWisselaar isPc={isPcLayout} bijWissel={() => setIsPcLayout(!isPcLayout)} />
          {/* @ts-ignore */}
          <img src="/logo.png" alt="NebulaTide" style={stijlen.logoPlaatje} />
        </View>
        <View style={stijlen.kopGolf} />
      </View>

      <ScrollView 
        contentContainerStyle={[
          stijlen.scrollInhoud,
          // @ts-ignore
          isPcLayout && { alignItems: 'center' }
        ]} 
        showsVerticalScrollIndicator={false}
      >
        <View style={[stijlen.inhoud, isPcLayout ? stijlen.inhoudPc : {}]}>
          {/* Zoekbalk */}
          <TouchableOpacity 
            style={stijlen.zoekBalk} 
            onPress={() => navigation.navigate('SearchTab', { screen: 'Search' })} 
            activeOpacity={0.8}
          >
            <Text style={stijlen.zoekIcoon}>🔍</Text>
            <Text style={stijlen.zoekPlaceholder}>{t('home.searchPlaceholder') || 'Zoek spullen...'}</Text>
          </TouchableOpacity>

          {/* Heldensectie - Klikbare streepjescode */}
          <TouchableOpacity 
            activeOpacity={0.9} 
            onPress={behandelScanKlik}
            style={stijlen.heldenSectie}
          >
            <View style={stijlen.heldenAchtergrond}>
              <View style={stijlen.heldenDecoCirkel1} />
              <View style={stijlen.heldenDecoCirkel2} />
            </View>
            
            <View style={stijlen.heldenInhoud}>
              {/* Bewegende barcode knop */}
              <Animated.View style={{ transform: [{ translateY: zweefAnimatie }, { scale: schaalAnimatie }] }}>
                 <BarcodeAnimatie isAanHetScannen={true} />
              </Animated.View>
            </View>
          </TouchableOpacity>



          {/* Uitgelichte producten */}
          <View style={stijlen.sectie}>
            <View style={stijlen.sectieKop}>
              <Text style={stijlen.sectieTitel}>🌿 {t('home.featuredProducts', 'Featured Products')}</Text>
              <TouchableOpacity onPress={() => navigation.navigate('SearchTab', { screen: 'Search', params: { autoSearch: true } })}>
                <Text style={stijlen.linkAllesZien}>{t('home.viewAll', 'View All')} →</Text>
              </TouchableOpacity>
            </View>
            
            <View style={isPcLayout ? stijlen.roosterContainer : undefined}>
              {uitgelichteProducten.length > 0 ? (
                uitgelichteProducten.map((product) => (
                  <ProductCardWeb 
                    key={product.id} 
                    product={product} 
                    onPress={() => navigeerNaarProduct(product)}
                    isPcLayout={isPcLayout}
                  />
                ))
              ) : (
                <View style={stijlen.ladenBak}>
                  <Text style={stijlen.ladenTekst}>{t('common.loading')}</Text>
                </View>
              )}
            </View>
          </View>

          {/* Beste merken */}
          <View style={stijlen.sectie}>
            <Text style={stijlen.sectieTitel}>⭐ {t('home.topBrands', 'Top Brands')}</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={stijlen.merkenScroll}>
              {[
                { id: '1', name: 'EcoLife', icon: '🌿', color: '#E8F5E9' },
                { id: '2', name: 'PureBase', icon: '💧', color: '#E3F2FD' },
                { id: '3', name: 'GreenCo', icon: '🌱', color: '#F1F8E9' },
                { id: '4', name: 'BioLove', icon: '💚', color: '#E0F2F1' },
                { id: '5', name: 'EarthPly', icon: '🌍', color: '#EFEBE9' },
                { id: '6', name: 'Solaris', icon: '☀️', color: '#FFFDE7' },
              ].map((merk) => (
                <TouchableOpacity key={merk.id} style={[stijlen.merkKaart, { backgroundColor: merk.color }]}>
                  <Text style={stijlen.merkIcoon}>{merk.icon}</Text>
                  <Text style={stijlen.merkNaam}>{merk.name}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Wat statistiekjes */}
          <View style={stijlen.statsSectie}>
            <View style={stijlen.statKaart}>
              <Text style={stijlen.statIcoon}>🌊</Text>
              <Text style={stijlen.statWaarde}>1,234+</Text>
              <Text style={stijlen.statLabel}>{t('home.stats.scanned', 'Items Scanned')}</Text>
            </View>
            <View style={stijlen.statKaart}>
              <Text style={stijlen.statIcoon}>🐠</Text>
              <Text style={stijlen.statWaarde}>89%</Text>
              <Text style={stijlen.statLabel}>{t('home.stats.ocean', 'Ocean Happy')}</Text>
            </View>
            <View style={stijlen.statKaart}>
              <Text style={stijlen.statIcoon}>♻️</Text>
              <Text style={stijlen.statWaarde}>456</Text>
              <Text style={stijlen.statLabel}>{t('home.stats.options', 'Good Options')}</Text>
            </View>
          </View>

          {/* Minigame Footer Link */}
          <TouchableOpacity 
            style={stijlen.minigameFooter}
            onPress={() => navigation.navigate('Minigame')}
            activeOpacity={0.7}
          >
            <View style={stijlen.minigameContent}>
              <Text style={stijlen.minigameIcon}>👾</Text>
              <View>
                <Text style={stijlen.minigameLabel}>Extra'tje: Ocean Battle</Text>
                <Text style={stijlen.minigameSub}>Speel een spelletje over de oceaan!</Text>
              </View>
            </View>
            <Text style={stijlen.minigameArrow}>→</Text>
          </TouchableOpacity>

          <View style={stijlen.legalFooter}>
            <View style={{flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center'}}>
                <Text style={stijlen.legalText}>
                    {t('legal.agreement_start', 'By using this app, you agree to our ')}
                </Text>
                <TouchableOpacity onPress={() => showModal(t('legal.termsTitle'), t('legal.termsText'))}>
                    <Text style={[stijlen.legalText, stijlen.legalLink]}>{t('legal.termsTitle')}</Text>
                </TouchableOpacity>
                <Text style={stijlen.legalText}>
                    {t('legal.agreement_middle', ' and ')}
                </Text>
                <TouchableOpacity onPress={() => showModal(t('legal.privacyTitle'), t('legal.privacyText'))}>
                    <Text style={[stijlen.legalText, stijlen.legalLink]}>{t('legal.privacyTitle')}</Text>
                </TouchableOpacity>
                <Text style={stijlen.legalText}>.</Text>
            </View>
          </View>
        </View>
      </ScrollView>

      {/* Modal Overlay */}
      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <TouchableOpacity 
            style={stijlen.modalOverlay} 
            activeOpacity={1} 
            onPress={() => setModalVisible(false)}
        >
          <TouchableOpacity 
            activeOpacity={1} 
            onPress={() => {}} 
            style={stijlen.modalContent}
          >
             <Text style={stijlen.modalTitle}>{modalContent.title}</Text>
             <ScrollView style={{maxHeight: 300}}>
                <Text style={stijlen.modalText}>{modalContent.text}</Text>
             </ScrollView>
             <TouchableOpacity 
                style={stijlen.closeButton}
                onPress={() => setModalVisible(false)}
             >
                <Text style={stijlen.closeButtonText}>{t('common.close', 'Sluiten')}</Text>
             </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// bapaalCijferKleur moved to top

const stijlen = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
    // minHeight: '100%' as any, // Removed to fix flex issue
  },
  kop: {
    position: 'relative',
    height: 160,
    overflow: 'hidden',
  },
  kopGradient: {
    flex: 1,
    // @ts-ignore
    backgroundImage: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 20,
  },
  logoPlaatje: {
    height: 80, 
    filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.2)) brightness(0) invert(1)',
    objectFit: 'contain'
  } as any,
  /* Tagline removed */
  kopGolf: {
    position: 'absolute',
    bottom: -1,
    left: 0,
    right: 0,
    height: 40,
    backgroundColor: '#F8FAFC',
    borderTopLeftRadius: 40,
    borderTopRightRadius: 40,
  },
  scrollInhoud: {
    flexGrow: 1,
    paddingBottom: 100,
  },
  inhoud: {
    paddingHorizontal: 20,
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  inhoudPc: {
    maxWidth: 1200,
  },
  roosterContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  productKaartPc: {
    width: '31%', // roughly 3 columns with gap
    minWidth: 250,
    flex: 1,
  },
  zoekBalk: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    marginBottom: 24,
    // @ts-ignore
    boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  zoekIcoon: {
    fontSize: 20,
    marginRight: 12,
  },
  zoekPlaceholder: {
    fontSize: 16,
    color: '#94A3B8',
  },
  heldenSectie: {
    backgroundColor: 'white',
    borderRadius: 24,
    overflow: 'hidden',
    marginBottom: 24,
    // @ts-ignore
    boxShadow: '0 10px 40px rgba(99, 102, 241, 0.15)',
    position: 'relative',
  },
  heldenAchtergrond: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  heldenDecoCirkel1: {
    position: 'absolute',
    top: -60,
    left: -60,
    width: 200,
    height: 200,
    borderRadius: 100,
    backgroundColor: 'rgba(99, 102, 241, 0.05)',
  },
  heldenDecoCirkel2: {
    position: 'absolute',
    bottom: -40,
    right: -40,
    width: 150,
    height: 150,
    borderRadius: 75,
    backgroundColor: 'rgba(139, 92, 246, 0.05)',
  },
  heldenInhoud: {
    padding: 32,
    alignItems: 'center',
    zIndex: 1,
  },
  minigameFooter: {
    marginTop: 40,
    paddingVertical: 16,
    paddingHorizontal: 24,
    backgroundColor: '#F1F5F9',
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    maxWidth: 400,
    alignSelf: 'center',
    width: '100%',
    cursor: 'pointer',
    // @ts-ignore
    transition: 'all 0.2s',
  },
  minigameContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  minigameIcon: {
    fontSize: 24,
    opacity: 0.8,
  },
  minigameLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#475569',
  },
  minigameSub: {
    fontSize: 12,
    color: '#94A3B8',
  },
  minigameArrow: {
    fontSize: 18,
    color: '#94A3B8',
    fontWeight: '300',
  },
  sectie: {
    marginBottom: 24,
  },
  sectieKop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectieTitel: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
  },
  linkAllesZien: {
    fontSize: 14,
    color: '#6366F1',
    fontWeight: '600',
  },
  productKaart: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    marginBottom: 12,
    // @ts-ignore
    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
    cursor: 'pointer',
    borderWidth: 1,
    borderColor: 'rgba(226, 232, 240, 0.8)',
    transform: [{ scale: 1 }],
    // @ts-ignore
    transition: 'all 0.2s ease',
  },
  productIcoon: {
    width: 50,
    height: 50,
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 14,
  },
  productInfo: {
    flex: 1,
  },
  productNaam: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 2,
  },
  productMerk: {
    fontSize: 14,
    color: '#64748B',
  },
  cijferBadge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cijferTekst: {
    color: 'white',
    fontWeight: '700',
    fontSize: 14,
  },
  ladenBak: {
    padding: 40,
    alignItems: 'center',
  },
  ladenTekst: {
    color: '#94A3B8',
    fontSize: 14,
  },
  merkenScroll: {
    marginTop: 8,
  },
  merkKaart: {
    width: 100,
    height: 100,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
    // @ts-ignore
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    cursor: 'pointer',
  },
  merkIcoon: {
    fontSize: 32,
    marginBottom: 6,
  },
  merkNaam: {
    fontSize: 12,
    fontWeight: '600',
    color: '#334155',
  },
  statsSectie: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  statKaart: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 16,
    alignItems: 'center',
    // @ts-ignore
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  statIcoon: {
    fontSize: 24,
    marginBottom: 8,
  },
  statWaarde: {
    fontSize: 20,
    fontWeight: '800',
    color: '#1E293B',
  },
  statLabel: {
    fontSize: 11,
    color: '#64748B',
    marginTop: 2,
    textAlign: 'center',
  },
  legalFooter: {
    marginTop: 40,
    marginBottom: 20,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  legalText: {
    fontSize: 12,
    color: '#94A3B8',
    textAlign: 'center',
    lineHeight: 18,
  },
  legalLink: {
    color: '#6366F1',
    fontWeight: '600',
    textDecorationLine: 'underline',
  } as any,
  /* Modal Stijlen */
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    // @ts-ignore
    cursor: 'default',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    // @ts-ignore
    boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 16,
    color: '#1E293B',
  },
  modalText: {
    fontSize: 14,
    color: '#475569',
    lineHeight: 22,
  },
  closeButton: {
    marginTop: 20,
    backgroundColor: '#6366F1',
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: 'center',
    cursor: 'pointer',
  },
  closeButtonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 14,
  }
});
